import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import {
  FaBookReader,
  FaBriefcase,
  FaChalkboardTeacher,
  FaChartBar,
  FaHome,
  FaStar,
  FaLayerGroup,
} from "react-icons/fa";
import { AiOutlineClose, AiOutlineMenu } from "react-icons/ai";
import useStore from "@/services/store";

import AppSelector from "@/components/SelectApp";
import { isInSelesquad } from "@/utils/roles";
import { FaPencil, FaPersonSwimming } from "react-icons/fa6";
import { HiOutlineAcademicCap, HiOutlineClipboardDocumentCheck, HiOutlineBookOpen, HiOutlineRocketLaunch } from "react-icons/hi2";

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium ${
    isActive ? "bg-gray-100 text-black shadow-inner" : "text-gray-700 hover:bg-gray-100 hover:text-black"
  }`;

const SectionDivider = ({ label }) => (
  <div className="flex items-center py-4 mb-1 ml-2">
    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</span>
    <span className="flex-1 border-t border-gray-200 ml-2"></span>
  </div>
);

export default function SelecoleSidebar() {
  const { sideBarOpen, setSideBarOpen } = useStore();
  const { user } = useSelector((state) => state.Auth);

  const isTrainee = user?.training_status === "in_training" || user?.training_status === "ready_for_evaluation";

  useEffect(() => {
    if (window.innerWidth < 768) setSideBarOpen(false);
  }, []);

  if (!sideBarOpen) {
    return (
      <nav className="w-12 h-screen flex flex-col border-r border-gray-200 items-center justify-start fixed top-0 left-0 z-30">
        <button type="button" id="menu-icon" onClick={() => setSideBarOpen(true)} className="mt-4">
          <AiOutlineMenu className="text-black text-2xl" />
        </button>
      </nav>
    );
  }

  // Trainee-specific simplified sidebar
  if (isTrainee) {
    return (
      <nav className="z-30 md:w-64 absolute top-0 left-0 ease-in-out duration-300">
        <div className="bg-white flex flex-col w-full h-screen overflow-y-scroll overflow-x-hidden border-r border-gray-200">
          <div className="flex justify-between items-center">
            <AppSelector userId={user._id} sideBarOpen={sideBarOpen} setSideBarOpen={setSideBarOpen} />
          </div>
          <button type="button" id="menu-icon" onClick={() => setSideBarOpen(false)} className="absolute top-2 right-2">
            <AiOutlineClose className="text-black text-lg transition-opacity duration-300 opacity-100" />
          </button>

          {/* Trainee status badge */}
          <div className="px-4 mb-2">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <HiOutlineRocketLaunch className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">In Training</span>
              </div>
              <p className="text-xs text-blue-600">
                {user.training_start_date
                  ? `Day ${Math.floor((new Date() - new Date(user.training_start_date)) / (1000 * 60 * 60 * 24)) + 1}`
                  : "Just started"}
              </p>
            </div>
          </div>

          <div className="space-y-2 flex-1">
            <div className="flex flex-col justify-between">
              <div className="px-4 pb-1">
                <ul className="space-y-1">
                  <SectionDivider label="My Training" />
                  <li>
                    <NavLink to="/selecole" end className={navLinkClass}>
                      <FaHome className="text-lg" />
                      <span>Home</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/selecole/career-path" className={navLinkClass}>
                      <FaBriefcase className="text-lg" />
                      <span>Career Paths</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/selecole/course" className={navLinkClass}>
                      <FaChalkboardTeacher className="text-lg" />
                      <span>Courses</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/selecole/learn" className={navLinkClass}>
                      <FaBookReader className="text-lg" />
                      <span>Workshops</span>
                    </NavLink>
                  </li>

                  <SectionDivider label="Resources" />
                  <li>
                    <NavLink to="/selecole/library" className={navLinkClass}>
                      <FaLayerGroup className="text-lg" />
                      <span>UI Library</span>
                    </NavLink>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Full sidebar for managers/admins
  return (
    <nav className="z-30 md:w-64 absolute top-0 left-0 ease-in-out duration-300">
      <div className="bg-white flex flex-col w-full h-screen overflow-y-scroll overflow-x-hidden border-r border-gray-200">
        <div className="flex justify-between items-center">
          <AppSelector userId={user._id} sideBarOpen={sideBarOpen} setSideBarOpen={setSideBarOpen} />
        </div>
        <button type="button" id="menu-icon" onClick={() => setSideBarOpen(false)} className="absolute top-2 right-2">
          <AiOutlineClose className="text-black text-lg transition-opacity duration-300 opacity-100" />
        </button>
        <div className="space-y-2 flex-1">
          <div className="flex flex-col justify-between">
            <div className="px-4 pb-1">
              <ul className="space-y-1">
                {/* ── Learning ── */}
                <SectionDivider label="Learning" />
                <li>
                  <NavLink to="/selecole" end className={navLinkClass}>
                    <FaHome className="text-lg" />
                    <span>Home</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/selecole/career-path" className={navLinkClass}>
                    <FaBriefcase className="text-lg" />
                    <span>Career Paths</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/selecole/course" className={navLinkClass}>
                    <FaChalkboardTeacher className="text-lg" />
                    <span>Courses</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/selecole/learn" className={navLinkClass}>
                    <FaBookReader className="text-lg" />
                    <span>Workshops</span>
                  </NavLink>
                </li>

                {/* ── Management (Selesquad) ── */}
                {isInSelesquad(user) && (
                  <>
                    <SectionDivider label="Management" />
                    <li>
                      <NavLink to="/selecole/tracker" className={navLinkClass}>
                        <FaPersonSwimming className="text-lg" />
                        <span>Piscine</span>
                      </NavLink>
                    </li>
                  </>
                )}

                {/* ── Content ── */}
                <SectionDivider label="Content" />
                {(isInSelesquad(user) || ["admin", "super_admin"].includes(user.role)) && (
                  <li>
                    <NavLink to="/selecole/editor" className={navLinkClass}>
                      <FaPencil className="text-lg" />
                      <span>Editor</span>
                    </NavLink>
                  </li>
                )}
                <li>
                  <NavLink to="/selecole/library" className={navLinkClass}>
                    <FaLayerGroup className="text-lg" />
                    <span>UI Library</span>
                  </NavLink>
                </li>

                {/* ── Admin ── */}
                {["admin", "super_admin"].includes(user.role) && (
                  <>
                    <SectionDivider label="Analytics" />
                    <li>
                      <NavLink to="/selecole/rating" className={navLinkClass}>
                        <FaStar className="text-lg" />
                        <span>Ratings</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/selecole/stats" className={navLinkClass}>
                        <FaChartBar className="text-lg" />
                        <span>Stats</span>
                      </NavLink>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
